import { hashToPostgrest } from '@/utils'
import { defaults } from 'lodash'
import { AxiosInstance, AxiosError } from 'axios'
import { ActionTree } from 'vuex'
import { Mutation } from './mutations'
import {
  DueTask,
  Gateway,
  ModuleState,
  Process,
  ProcessLog,
  RootState,
  RunningProcess,
  TaskLog,
  VariablePayload,
} from './types'

export const actions: ActionTree<ModuleState, RootState> = {}

export interface SendMessageAction {
  type: 'sendMessage' | 'workflow/sendMessage'
  messageName: string
  all?: boolean
  businessKey?: string
  processInstanceId?: string
  processVariables?: VariablePayload
  resultEnabled?: boolean
}

const handleError = (error: any) => {
  const axiosError = error as AxiosError
  if (axiosError.response) {
    window.alert(`error ${axiosError.response.status}: ${JSON.stringify(axiosError.response.data)}`)
  }
}

actions.sendMessage = async ({ dispatch, rootGetters }, payload: SendMessageAction) => {
  const http: AxiosInstance = rootGetters.http
  return http
    .post('/api/workflow/rest/message', defaults(payload, { resultEnabled: true, all: true }))
    .then(({ status, data }) => {
      if (status === 204 || status === 200) {
        return dispatch<FetchAction>({
          type: 'fetch',
        })
      } else {
        throw data
      }
    })
    .catch(handleError)
}

export interface SendSignalAction {
  type: 'sendSignal' | 'workflow/sendSignal'
  name: string
  executionId?: string
  variables?: VariablePayload
}

actions.sendSignal = async ({ dispatch, rootGetters }, payload: SendSignalAction) => {
  const http: AxiosInstance = rootGetters.http
  return http
    .post('/api/workflow/rest/signal', payload)
    .then(({ status, data }) => {
      if (status === 204 || status === 200) {
        return dispatch<FetchAction>({
          type: 'fetch',
        })
      } else {
        throw data
      }
    })
    .catch(handleError)
}

export interface CancelProcessAction {
  type: 'cancelProcess' | 'workflow/cancelProcess'
  process: Pick<RunningProcess, 'id'>
  skipListeners?: boolean
  skipMappings?: boolean
}

actions.cancelProcess = async (
  { dispatch, rootGetters },
  { process, skipListeners = true, skipMappings = true }: CancelProcessAction
) => {
  const http: AxiosInstance = rootGetters.http
  return http
    .delete(`/api/workflow/rest/process-instance/${process.id}`, {
      params: {
        skipCustomListeners: skipListeners,
        skipIoMappings: skipMappings,
      },
    })
    .then(({ status, data }) => {
      if (status === 204) {
        return dispatch<FetchAction>({
          type: 'fetch',
        })
      } else {
        throw data
      }
    })
    .catch(handleError)
}

export interface CompleteTaskAction {
  type: 'completeTask' | 'workflow/completeTask'
  task: Pick<DueTask, 'id'>
  variables?: VariablePayload
}

actions.completeTask = async (
  { commit, dispatch, rootGetters },
  { task, variables = {} }: CompleteTaskAction
) => {
  const http: AxiosInstance = rootGetters.http
  const url: string = `/api/workflow/rest/task/${task.id}/submit-form`
  return (variables ? http.post(url, { variables }) : http.post(url))
    .then(({ status, data }) => {
      if (status === 204) {
        commit<Mutation>({ type: 'dropDueTasks', tasks: [task] })
        return dispatch<FetchAction>({
          type: 'fetch',
        })
      } else {
        throw data
      }
    })
    .catch(handleError)
}

export interface ResetAction {
  type: 'reset' | 'workflow/reset'
}

actions.reset = async ({ commit }, {  }: ResetAction) => {
  return commit<Mutation>({ type: 'resetState' })
}

export interface FetchAction {
  type: 'fetch' | 'workflow/fetch'
}

actions.fetch = async ({ commit, dispatch }) => {
  commit('setLoading', true)
  return Promise.all([
    dispatch<FetchTaskLogAction>({ type: 'fetchTaskLog' }),
    dispatch<FetchProcessesAction>({ type: 'fetchProcesses' }),
    dispatch<FetchProcessLogAction>({ type: 'fetchProcessLog' }),
    dispatch<FetchGatewaysAction>({ type: 'fetchGateways' }),
    dispatch<FetchAppointmentsAction>({ type: 'fetchAppointments' }),
  ])
    .catch(error => {
      console.error('workflow/fetch failed')
      console.error(error)
    })
    .finally(() => {
      commit('setLoading', false)
    })
}

export interface FetchAppointmentsAction {
  type: 'fetchAppointments' | 'workflow/fetchAppointments'
}

import { SetMyDateMutation } from './mutations'
import { MyDate } from './types'
import { BIconFileEarmarkSpreadsheet } from 'bootstrap-vue'

actions.fetchAppointments = async ({ commit, rootGetters }, { type }: FetchAppointmentsAction) => {
  const http: AxiosInstance = rootGetters.http
  const dates = await http.get<MyDate[]>('/api/my_dates').then(({ data }) => {
    commit<SetMyDateMutation>({ type: 'setMyDates', dates: data })
  })
}

export type FetchTaskLogAction = {
  type: 'fetchTaskLog' | 'workflow/fetchTaskLog'
} & Partial<
  Pick<
    TaskLog,
    | 'id'
    | 'task_definition_key'
    | 'business_key'
    | 'process_definition_key'
    | 'process_instance_id'
    | 'case_id'
  >
>

actions.fetchTaskLog = async (
  { commit, getters, rootGetters },
  { type, ...params }: FetchTaskLogAction
) => {
  const http: AxiosInstance = rootGetters.http
  const latestDoneTask: TaskLog | undefined = getters.latestDoneTask

  const commitTasks = ({ data }: { data: TaskLog[] }) => {
    commit<Mutation>({ type: 'addTaskLog', tasks: data })
  }

  // we have a latest task, fetch what's new
  if (latestDoneTask && latestDoneTask.end_time) {
    return http
      .get<TaskLog[]>(
        `/api/my_task_log?or=(end_time.is.null,end_time.gte.${encodeURIComponent(
          latestDoneTask.end_time
        )})`
      )
      .then(commitTasks)
  }

  // we have no latest task, fetch all recent tasks and max 2000 done tasks
  else {
    return Promise.all([
      http.get<TaskLog[]>('/api/my_task_log?end_time=is.null').then(commitTasks),
      http
        .get<TaskLog[]>('/api/my_task_log?end_time=not.is.null&order=end_time.desc&limit=2000')
        .then(commitTasks),
    ])
  }
}

export type FetchGatewaysAction = {
  type: 'fetchGateways' | 'workflow/fetchGateways'
} & Partial<Pick<Gateway, 'id' | 'business_key' | 'activity_id' | 'process_instance_id'>>

actions.fetchGateways = async (
  { commit, rootGetters },
  { type, ...params }: FetchGatewaysAction
) => {
  const http: AxiosInstance = rootGetters.http
  const postgrestParams = hashToPostgrest(params)
  return http.get<Gateway[]>('/api/my_gateways', { params: postgrestParams }).then(({ data }) => {
    commit<Mutation>({ type: 'addGateway', gateways: data })
  })
}

export interface FetchProcessesAction {
  type: 'fetchProcesses' | 'workflow/fetchProcesses'
  businessKey?: Process['business_key']
  caseId?: Process['case_id']
  definitionKey?: Process['definition_key']
  id?: Process['id']
}

actions.fetchProcesses = async (
  { commit, rootGetters },
  { type, ...params }: FetchProcessesAction
) => {
  const http: AxiosInstance = rootGetters.http
  const postgrestParams = hashToPostgrest(params)
  return http
    .get<RunningProcess[]>('/api/my_running_processes', {
      params: postgrestParams,
    })
    .then(({ data: processes }) => {
      commit<Mutation>({ type: 'addProcesses', processes })
      return processes
    })
}

export interface FetchProcessLogAction {
  type: 'fetchProcessLog' | 'workflow/fetchProcessLog'
  businessKey?: Process['business_key']
  caseId?: Process['case_id']
  definitionKey?: Process['definition_key']
  id?: Process['id']
}

actions.fetchProcessLog = async (
  { commit, rootGetters },
  { type, ...params }: FetchProcessLogAction
) => {
  const http: AxiosInstance = rootGetters.http
  const postgrestParams = hashToPostgrest(params)
  return http
    .get<ProcessLog[]>('/api/my_process_log', {
      params: postgrestParams,
    })
    .then(({ data: processes }) => {
      commit<Mutation>({ type: 'addProcessLog', processes })
      return processes
    })
}
export interface SkipExecutionAction {
  type: 'skipExecution' | 'workflow/skipExecution'
  execution: { execution_id: string }
  variables?: VariablePayload
}

actions.skipExecution = async (
  { dispatch, rootGetters },
  { execution, variables = {} }: SkipExecutionAction
) => {
  const http: AxiosInstance = rootGetters.http
  const url: string = `/api/workflow/rest/execution/${execution.execution_id}/signal`
  return (variables ? http.post(url, { variables }) : http.post(url)).then(({ status, data }) => {
    if (status === 204) {
      return dispatch<FetchAction>({
        type: 'fetch',
      })
    } else {
      throw data
    }
  })
}

export interface UpdateDueDateAction {
  type: 'updateDueDate' | 'workflow/updateDueDate'
  jobId: string
  dueDate: Date
}

actions.updateDueDate = async (
  { dispatch, rootGetters },
  { jobId, dueDate }: UpdateDueDateAction
) => {
  const http: AxiosInstance = rootGetters.http
  const url: string = `/api/workflow/rest/job/${jobId}/duedate`
  return http
    .put(url, { duedate: dueDate.toISOString().replace('Z', '+0000') })
    .then(({ status, data }) => {
      if (status === 204) {
        return dispatch<FetchAction>({
          type: 'fetch',
        })
      } else {
        throw data
      }
    })
}

export interface StartProcessAction {
  type: 'startProcess' | 'workflow/startProcess'
  definitionKey: string
  businessKey: string
  caseId?: string
  variables?: any
  startInstructions?: any[]
}

actions.startProcess = async (
  { dispatch, commit, rootGetters },
  {
    type,
    definitionKey,
    businessKey,
    caseId,
    variables: vars,
    startInstructions,
  }: StartProcessAction
) => {
  const http: AxiosInstance = rootGetters.http
  const url = `/api/workflow/rest/process-definition/key/${encodeURIComponent(definitionKey)}/start`
  const variables = vars || {}
  if (caseId) {
    variables.caseId = { type: 'String', value: caseId }
  }

  return http.post(url, { businessKey, variables, startInstructions }).then(() => {
    return dispatch<FetchAction>({
      type: 'fetch',
    })
  })
}

actions.USER_SIGNED_IN = {
  root: true,
  async handler({ dispatch, commit }) {
    commit<Mutation>({ type: 'resetState' })
    return Promise.all([dispatch<FetchAction>({ type: 'fetch' })])
  },
}

actions.USER_LOGGED_OUT = {
  root: true,
  handler({ commit }) {
    commit<Mutation>({ type: 'resetState' })
  },
}

export type Action =
  | CancelProcessAction
  | CompleteTaskAction
  | FetchAction
  | FetchProcessesAction
  | FetchTaskLogAction
  | FetchGatewaysAction
  | StartProcessAction
  | SkipExecutionAction
  | ResetAction
  | SendMessageAction
  | UpdateDueDateAction

export default actions
